import { useEffect, useMemo, useState } from 'react';
import { Money, Text } from '@belong/ui';
import { format, parseISO } from 'date-fns';
import { clsx } from 'clsx';
import { IconCheck2, IconDots } from '@belong/icons';
import { PAYMENT_SCREENS } from 'containercomponents/Modals/PaymentsModal/PaymentsModal';
import PaymentMenuSelectionContainer from 'containers/PaymentSelectionMenuContainer/PaymentMenuSelectionContainer';
import { PaymentPreference } from 'models/enums';
import { FEEDBACK_STRINGS } from 'strings/errors.string';
import type {
  ConsumerDeductFromRentPreviewModel,
  ConsumerFailedPaymentModel,
  ConsumerPaymentMethodSummaryModel,
  ConsumerPaymentRescheduleModel,
  ConsumerPaymentRescheduleTotalChargeModel,
} from '../failed-payments.types';
import type { PaymentAccountResourceModel } from 'api/models';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import {
  formatFailedPaymentsAmount,
  formatLateFee,
  formatStreetAddress,
  getBalanceUniqueIds,
  mapPaymentAccountToPaymentMethodSummary,
} from '../failed-payments-utils';
import { PaymentMethodSummary } from '../payment-method-summary';

export const DEDUCT_REMAINING_RIGHT_NOW = 'right-now';

const REMAINING_DAYS_AHEAD = 5;
const PAYOUT_DATE_FORMAT = 'MMM d';
const PAYOUT_MONTH_FORMAT = 'MMM. yyyy';
const SCHEDULED_RENT_DATE_FORMAT = 'MMM do';

type ApiClient = {
  post: <T>(url: string, body: unknown) => Promise<T>;
};

function formatPayoutDate(iso: string, dateFormat = PAYOUT_DATE_FORMAT) {
  const date = parseISO(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return format(date, dateFormat);
}

function fill(template: string, values: Record<string, string>) {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, value),
    template,
  );
}

type DeductFullCoverageVariant = 'held' | 'scheduled' | 'full-payout';

function getDeductFullCoverageVariant(
  preview: ConsumerDeductFromRentPreviewModel,
): DeductFullCoverageVariant {
  if (preview.payoutAmountAfterDeduction <= 0) return 'full-payout';
  return preview.rentSource === 'ScheduledRent' ? 'scheduled' : 'held';
}

export function getDeductFooterCtaLabel(
  preview: ConsumerDeductFromRentPreviewModel,
  remainingChargeAmount: number,
): string {
  if (preview.remainingAmount > 0) {
    return fill(FAILED_PAYMENTS_STRINGS['deduct.footer_cta'], {
      amount: formatFailedPaymentsAmount(remainingChargeAmount),
    });
  }

  const variant = getDeductFullCoverageVariant(preview);

  if (variant === 'full-payout') {
    return fill(FAILED_PAYMENTS_STRINGS['deduct.footer_cta_full_payout'], {
      date: formatPayoutDate(preview.payoutDate),
    });
  }

  return fill(
    FAILED_PAYMENTS_STRINGS[
      variant === 'scheduled'
        ? 'deduct.footer_cta_scheduled'
        : 'deduct.footer_cta_held'
    ],
    { amount: formatFailedPaymentsAmount(preview.appliedAmount) },
  );
}

type DeductFromRentSubsectionProps = {
  apiClient: ApiClient;
  homeAddress: string | null;
  failedPayments: ConsumerFailedPaymentModel[];
  balanceUniqueIds: string[];
  preview: ConsumerDeductFromRentPreviewModel | null;
  previewLoading: boolean;
  previewError: boolean;
  paymentMethods: PaymentAccountResourceModel[];
  paymentAccountUniqueId: string | null;
  paymentMethod: ConsumerPaymentMethodSummaryModel | null | undefined;
  creditCardFeeAmount?: number;
  selectedRemainingDate: string | null;
  onSelectRemainingDate: (date: string) => void;
  onSelectPaymentMethod: (id: string) => void;
};

export function DeductFromRentSubsection({
  apiClient,
  homeAddress,
  failedPayments,
  balanceUniqueIds,
  preview,
  previewLoading,
  previewError,
  paymentMethods,
  paymentAccountUniqueId,
  paymentMethod,
  creditCardFeeAmount,
  selectedRemainingDate,
  onSelectRemainingDate,
  onSelectPaymentMethod,
}: DeductFromRentSubsectionProps) {
  if (previewLoading) {
    return null;
  }

  if (previewError || !preview) {
    return (
      <div className="flex flex-col gap-xs rounded border border-solid border-red bg-[rgba(255,86,97,0.04)] p-md">
        <Text variant="p1" className="text-navy">
          {FAILED_PAYMENTS_STRINGS['deduct.error.preview']}
        </Text>
      </div>
    );
  }

  if (preview.remainingAmount > 0) {
    return (
      <PartialCoverage
        apiClient={apiClient}
        homeAddress={homeAddress}
        failedPayments={failedPayments}
        balanceUniqueIds={balanceUniqueIds}
        preview={preview}
        paymentMethods={paymentMethods}
        paymentAccountUniqueId={paymentAccountUniqueId}
        paymentMethod={paymentMethod}
        creditCardFeeAmount={creditCardFeeAmount}
        selectedRemainingDate={selectedRemainingDate}
        onSelectRemainingDate={onSelectRemainingDate}
        onSelectPaymentMethod={onSelectPaymentMethod}
      />
    );
  }

  return (
    <FullCoverageCallout
      preview={preview}
      homeAddress={homeAddress}
      failedPayments={failedPayments}
    />
  );
}

function CalloutRow({
  label,
  amount,
  amountClassName,
  note,
  labelSemibold,
}: {
  label: string;
  amount: number;
  amountClassName?: string;
  note?: string;
  labelSemibold?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-md">
      <span className="flex min-w-0 flex-col gap-2xs">
        <Text
          fontWeight={labelSemibold ? 'semibold' : 'regular'}
          className="text-navy"
        >
          {label}
        </Text>
        {note ? (
          <Text variant="p1" className="text-dark-gray">
            {note}
          </Text>
        ) : null}
      </span>
      <Money
        value={amount}
        format="DEFAULT"
        className={clsx(
          'shrink-0 text-body font-semibold',
          amountClassName ?? 'text-navy',
        )}
      />
    </div>
  );
}

function FullCoverageCallout({
  preview,
  homeAddress,
  failedPayments,
}: {
  preview: ConsumerDeductFromRentPreviewModel;
  homeAddress: string | null;
  failedPayments: ConsumerFailedPaymentModel[];
}) {
  const home = formatStreetAddress(homeAddress);
  const payoutDate = formatPayoutDate(preview.payoutDate);
  const payoutMonth = formatPayoutDate(preview.payoutDate, PAYOUT_MONTH_FORMAT);
  const isScheduledRent = preview.rentSource === 'ScheduledRent';
  const isFullPayoutConsumed = preview.payoutAmountAfterDeduction <= 0;
  const payoutAppliedAmount = Math.max(0, preview.originalPayoutAmount);
  const amountBeyondPayout = Math.max(
    0,
    preview.appliedAmount - payoutAppliedAmount,
  );
  const isExactFullPayout =
    isFullPayoutConsumed && payoutAppliedAmount > 0 && amountBeyondPayout === 0;

  const variant = getDeductFullCoverageVariant(preview);
  const headline =
    variant === 'full-payout'
      ? fill(FAILED_PAYMENTS_STRINGS['deduct.no_payout_title'], {
          date: payoutDate,
        })
      : variant === 'scheduled'
        ? fill(FAILED_PAYMENTS_STRINGS['deduct.scheduled_title'], {
            payout: formatFailedPaymentsAmount(
              preview.payoutAmountAfterDeduction,
            ),
            date: payoutDate,
            original: formatFailedPaymentsAmount(preview.originalPayoutAmount),
          })
        : FAILED_PAYMENTS_STRINGS['deduct.held_title'];

  const nextPayoutDate = preview.nextPayoutDate
    ? formatPayoutDate(preview.nextPayoutDate)
    : '';

  const note = isExactFullPayout
    ? fill(FAILED_PAYMENTS_STRINGS['deduct.no_payout_note'], {
        date: payoutDate,
        amount: formatFailedPaymentsAmount(payoutAppliedAmount),
        nextDate: nextPayoutDate,
      })
    : isFullPayoutConsumed && payoutAppliedAmount > 0
      ? fill(FAILED_PAYMENTS_STRINGS['deduct.no_payout_note_carryover'], {
          date: payoutDate,
          amount: formatFailedPaymentsAmount(payoutAppliedAmount),
          remaining: formatFailedPaymentsAmount(amountBeyondPayout),
        })
      : isFullPayoutConsumed
        ? fill(FAILED_PAYMENTS_STRINGS['deduct.no_payout_note_unavailable'], {
            date: payoutDate,
          })
        : isScheduledRent && (preview.lateFeeAmount ?? 0) > 0
          ? fill(FAILED_PAYMENTS_STRINGS['deduct.scheduled_late_fee_note'], {
              fee: formatLateFee(preview.lateFeeAmount ?? 0),
            })
          : null;

  const rentLabel = isScheduledRent
    ? fill(FAILED_PAYMENTS_STRINGS['deduct.rent_scheduled'], {
        date: formatPayoutDate(preview.payoutDate, SCHEDULED_RENT_DATE_FORMAT),
        home,
      })
    : isFullPayoutConsumed
      ? fill(FAILED_PAYMENTS_STRINGS['deduct.rent_collected'], {
          home,
          date: payoutDate,
        })
      : fill(FAILED_PAYMENTS_STRINGS['deduct.rent_available'], { home });

  const appliedRows = failedPayments
    .filter((failedPayment) => getBalanceUniqueIds(failedPayment).length > 0)
    .map((failedPayment) => ({
      key: getBalanceUniqueIds(failedPayment)[0] ?? '',
      label: failedPayment.label ?? '',
      amount: Math.abs(failedPayment.amount ?? 0),
    }));
  const appliedRowsTotal = appliedRows.reduce(
    (sum, row) => sum + row.amount,
    0,
  );
  const canItemiseApplied =
    appliedRows.length > 0 &&
    Math.abs(appliedRowsTotal - preview.appliedAmount) < 0.01;
  const appliedBalanceTitles = appliedRows
    .map((row) => row.label)
    .filter(Boolean)
    .join(', ');
  const appliedLabel =
    appliedBalanceTitles ||
    (isScheduledRent || isFullPayoutConsumed
      ? FAILED_PAYMENTS_STRINGS['deduct.applied_to_this_balance']
      : FAILED_PAYMENTS_STRINGS['deduct.applied_to_your_balance']);

  const showWhatChangesTag = isScheduledRent || isFullPayoutConsumed;

  return (
    <div
      className={clsx(
        'flex flex-col gap-sm rounded border border-solid p-sm',
        isFullPayoutConsumed
          ? 'border-gray bg-light-gray'
          : 'border-green bg-green-translucent-light',
      )}
    >
      <div className="flex flex-col gap-xs">
        {showWhatChangesTag ? (
          <span className="flex items-center gap-2xs">
            {!isFullPayoutConsumed && (
              <IconCheck2 width={14} height={14} className="text-green" />
            )}
            <Text
              variant="p1"
              fontWeight="semibold"
              className={isFullPayoutConsumed ? 'text-navy' : 'text-dark-green'}
            >
              {FAILED_PAYMENTS_STRINGS['deduct.what_changes']}
            </Text>
          </span>
        ) : null}
        <Text variant="h3" fontWeight="semibold" className="text-navy">
          {headline}
        </Text>
        {note ? (
          <Text variant="p1" className="text-navy">
            {note}
          </Text>
        ) : null}
      </div>

      <div className="border-0 border-t border-dashed border-gray" />

      <div className="flex flex-col gap-sm">
        <CalloutRow label={rentLabel} amount={preview.rentAmount} />
        <CalloutRow
          label={fill(FAILED_PAYMENTS_STRINGS['deduct.management_fee'], {
            month: payoutMonth,
          })}
          amount={-preview.managementFeeAmount}
        />
        {preview.financeInstallmentAmount > 0 ? (
          <CalloutRow
            label={fill(FAILED_PAYMENTS_STRINGS['deduct.finance_installment'], {
              month: payoutMonth,
            })}
            amount={-preview.financeInstallmentAmount}
          />
        ) : null}
        {canItemiseApplied ? (
          appliedRows.map((row) => (
            <CalloutRow key={row.key} label={row.label} amount={-row.amount} />
          ))
        ) : (
          <CalloutRow label={appliedLabel} amount={-preview.appliedAmount} />
        )}
      </div>

      <div className="border-0 border-t border-dashed border-gray" />

      <CalloutRow
        label={fill(FAILED_PAYMENTS_STRINGS['deduct.payout_line'], {
          date: payoutDate,
        })}
        labelSemibold
        note={
          isExactFullPayout || !isFullPayoutConsumed
            ? FAILED_PAYMENTS_STRINGS['deduct.payout_note']
            : payoutAppliedAmount > 0
              ? FAILED_PAYMENTS_STRINGS['deduct.payout_note_carryover']
              : FAILED_PAYMENTS_STRINGS['deduct.payout_note_unavailable']
        }
        amount={preview.payoutAmountAfterDeduction}
        amountClassName={isFullPayoutConsumed ? 'text-dark-gray' : 'text-green'}
      />
    </div>
  );
}

function PartialCoverage({
  apiClient,
  homeAddress,
  balanceUniqueIds,
  preview,
  paymentMethods,
  paymentAccountUniqueId,
  paymentMethod,
  creditCardFeeAmount,
  selectedRemainingDate,
  onSelectRemainingDate,
  onSelectPaymentMethod,
}: Omit<
  DeductFromRentSubsectionProps,
  'previewLoading' | 'previewError' | 'preview'
> & {
  preview: ConsumerDeductFromRentPreviewModel;
}) {
  const home = formatStreetAddress(homeAddress);
  const remainingAmount = preview.remainingAmount;
  const feeAmount = creditCardFeeAmount ?? 0;
  const chargingToday = remainingAmount + feeAmount;
  const isChargingNow = selectedRemainingDate === DEDUCT_REMAINING_RIGHT_NOW;
  const selectedRemainingDateLabel =
    !isChargingNow && selectedRemainingDate
      ? format(parseISO(selectedRemainingDate), 'EEE, MMM d, yyyy')
      : '';

  const dates = useMemo(() => {
    const arr: { iso: string; label: string }[] = [];
    const base = new Date();
    for (let i = 1; i <= REMAINING_DAYS_AHEAD; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      arr.push({
        iso: format(d, 'yyyy-MM-dd'),
        label: format(d, 'EEE, MMM d, yyyy'),
      });
    }
    return arr;
  }, []);

  const [dateFees, setDateFees] = useState<Record<string, number>>({});
  const datesKey = dates.map((d) => d.iso).join(',');
  const balancesKey = balanceUniqueIds.join(',');

  useEffect(() => {
    if (!paymentAccountUniqueId || balanceUniqueIds.length === 0) {
      setDateFees({});
      return;
    }
    let cancelled = false;

    (async () => {
      const results = await Promise.all(
        dates.map(async ({ iso }) => {
          try {
            const totals: ConsumerPaymentRescheduleTotalChargeModel[] =
              await Promise.all(
                balanceUniqueIds.map((id) =>
                  apiClient.post<ConsumerPaymentRescheduleTotalChargeModel>(
                    `/v2/users/balances/${id}/payment-reschedule/total`,
                    {
                      retryStrategy: 'ScheduleForLater',
                      scheduledRetryDate: iso,
                      paymentAccountUniqueId,
                    } as ConsumerPaymentRescheduleModel,
                  ),
                ),
              );
            const lateFee = totals.reduce(
              (sum, t) => sum + (t?.lateFeeCharge ?? 0),
              0,
            );
            return [iso, lateFee] as const;
          } catch {
            return [iso, 0] as const;
          }
        }),
      );
      if (cancelled) return;
      setDateFees(Object.fromEntries(results));
    })();

    return () => {
      cancelled = true;
    };
  }, [apiClient, balancesKey, paymentAccountUniqueId, datesKey]);

  const availablePaymentMethods = paymentMethods.filter(
    (method) => method.accountId && method.isVerified,
  );
  const selectedPaymentMethod =
    availablePaymentMethods.find(
      (method) => method.accountId === paymentAccountUniqueId,
    ) ?? availablePaymentMethods[0];
  const selectedPaymentMethodSummary = selectedPaymentMethod
    ? mapPaymentAccountToPaymentMethodSummary(selectedPaymentMethod)
    : paymentMethod;

  useEffect(() => {
    if (!paymentAccountUniqueId && selectedPaymentMethod?.accountId) {
      onSelectPaymentMethod(selectedPaymentMethod.accountId);
    }
  }, [
    onSelectPaymentMethod,
    paymentAccountUniqueId,
    selectedPaymentMethod?.accountId,
  ]);

  const paymentModalProps = {
    screen: PAYMENT_SCREENS.PAYMENT_SELECTION,
    errorScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.error.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.error.ctaLabel,
    },
    successScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.success.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.success.ctaLabel,
    },
  };

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-sm">
        <Text variant="h3" fontWeight="semibold">
          {FAILED_PAYMENTS_STRINGS['deduct.partial_title']}
        </Text>
        <div className="border-0 border-t border-dashed border-gray" />
        <div className="flex items-center justify-between">
          <Text className="text-navy">
            {fill(FAILED_PAYMENTS_STRINGS['deduct.covered_by_rent'], { home })}
          </Text>
          <Money
            value={-preview.appliedAmount}
            format="DOLLARS_NO_CENTS"
            className="shrink-0 text-body font-semibold text-navy"
          />
        </div>
        <div className="border-0 border-t border-dashed border-gray" />
        <div className="flex items-center justify-between">
          <Text fontWeight="semibold" className="text-navy">
            {FAILED_PAYMENTS_STRINGS['deduct.remaining_balance']}
          </Text>
          <Money
            value={remainingAmount}
            format="DOLLARS_NO_CENTS"
            className="shrink-0 text-body font-semibold text-navy"
          />
        </div>
      </div>

      <div className="flex flex-col gap-sm">
        <Text variant="h3" fontWeight="semibold">
          {fill(FAILED_PAYMENTS_STRINGS['deduct.charge_remaining_title'], {
            amount: formatFailedPaymentsAmount(remainingAmount),
          })}
        </Text>
        <div className="grid grid-cols-1 gap-sm sm:grid-cols-2 md:grid-cols-3">
          <button
            type="button"
            onClick={() => onSelectRemainingDate(DEDUCT_REMAINING_RIGHT_NOW)}
            className={clsx(
              'flex h-[64px] w-full cursor-pointer flex-col items-center justify-center rounded border-0 px-sm shadow transition-colors',
              isChargingNow ? 'bg-navy' : 'bg-white',
            )}
          >
            <Text
              variant="body"
              fontWeight={isChargingNow ? 'semibold' : 'regular'}
              className={isChargingNow ? 'text-white' : 'text-navy'}
            >
              {FAILED_PAYMENTS_STRINGS['deduct.right_now']}
            </Text>
          </button>
          {dates.map(({ iso, label }) => {
            const isSelected = selectedRemainingDate === iso;
            const fee = dateFees[iso] ?? 0;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => onSelectRemainingDate(iso)}
                className={clsx(
                  'flex h-[64px] w-full cursor-pointer flex-col items-center justify-center rounded border-0 px-sm shadow transition-colors',
                  isSelected ? 'bg-navy' : 'bg-white',
                )}
              >
                <Text
                  variant="body"
                  fontWeight={isSelected ? 'semibold' : 'regular'}
                  className={isSelected ? 'text-white' : 'text-navy'}
                >
                  {label}
                </Text>
                {fee > 0 && (
                  <Text
                    variant="p2"
                    fontWeight="semibold"
                    className="text-dark-gray"
                  >
                    + {formatLateFee(fee)}{' '}
                    {FAILED_PAYMENTS_STRINGS['schedule_later.late_fee_label']}
                  </Text>
                )}
              </button>
            );
          })}
        </div>

        {selectedPaymentMethodSummary && (
          <div className="w-full">
            <PaymentMenuSelectionContainer
              selectedPaymentMethodId={paymentAccountUniqueId ?? ''}
              onSelectPaymentMethod={onSelectPaymentMethod}
              paymentType={PaymentPreference.Maintenance}
              paymentModalProps={paymentModalProps}
              showOnlyValidPaymentMethods
              shouldShowAutoSplitPaymentMethod={false}
              onSelectAutoSplitPaymentMethod={() => {}}
              fitToButton
              onTop
              menuButtonLabel={
                <span className="flex h-[106px] w-full items-center rounded border border-solid border-gray bg-white px-[17px] text-left">
                  <span className="min-w-0 flex-1">
                    <PaymentMethodSummary
                      paymentMethod={selectedPaymentMethodSummary}
                    />
                  </span>
                  <span className="inline-flex px-xs text-navy">
                    <IconDots width={5} height={24} />
                  </span>
                </span>
              }
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-sm">
        <Text variant="h3" fontWeight="semibold">
          {FAILED_PAYMENTS_STRINGS['deduct.summary_title']}
        </Text>
        <div className="flex flex-col gap-sm">
          <SummaryRow
            label={FAILED_PAYMENTS_STRINGS['deduct.summary_rent_holding']}
            amount={preview.rentAmount}
          />
          <SummaryRow
            label={FAILED_PAYMENTS_STRINGS['deduct.summary_deducting']}
            amount={-preview.appliedAmount}
            semibold
          />
        </div>
        <div className="my-sm border-0 border-t border-dashed border-gray" />
        <div className="flex flex-col gap-sm">
          <SummaryRow
            label={
              isChargingNow
                ? FAILED_PAYMENTS_STRINGS['deduct.summary_immediate']
                : fill(FAILED_PAYMENTS_STRINGS['deduct.summary_scheduled'], {
                    date: selectedRemainingDateLabel,
                  })
            }
            amount={-remainingAmount}
          />
          {feeAmount > 0 && (
            <SummaryRow
              label={FAILED_PAYMENTS_STRINGS['deduct.summary_cc_fee']}
              amount={-feeAmount}
            />
          )}
          <SummaryRow
            label={
              isChargingNow
                ? FAILED_PAYMENTS_STRINGS['deduct.summary_charging_today']
                : fill(FAILED_PAYMENTS_STRINGS['deduct.summary_charging_on'], {
                    date: selectedRemainingDateLabel,
                  })
            }
            amount={-chargingToday}
            semibold
          />
        </div>
        <div className="my-sm border-0 border-t border-dashed border-gray" />
        <div className="flex items-start justify-between gap-md">
          <Text variant="h3" fontWeight="semibold" className="text-navy">
            {FAILED_PAYMENTS_STRINGS['deduct.summary_total']}
          </Text>
          <span className="flex flex-col items-end gap-2xs">
            <Money
              value={-(preview.appliedAmount + chargingToday)}
              format="DOLLARS_WITH_CENTS"
              className="text-h3-sm font-semibold text-navy md:text-h3-xl"
            />
            <Text variant="p1" className="text-right text-dark-gray">
              {isChargingNow
                ? fill(FAILED_PAYMENTS_STRINGS['deduct.summary_total_note'], {
                    rent: formatFailedPaymentsAmount(preview.appliedAmount),
                    card: formatFailedPaymentsAmount(chargingToday),
                  })
                : fill(
                    FAILED_PAYMENTS_STRINGS[
                      'deduct.summary_total_note_scheduled'
                    ],
                    {
                      rent: formatFailedPaymentsAmount(preview.appliedAmount),
                      card: formatFailedPaymentsAmount(chargingToday),
                      date: selectedRemainingDateLabel,
                    },
                  )}
            </Text>
          </span>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  amount,
  semibold,
}: {
  label: string;
  amount: number;
  semibold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-md">
      <Text
        fontWeight={semibold ? 'semibold' : 'regular'}
        className="text-navy"
      >
        {label}
      </Text>
      <Money
        value={amount}
        format="DOLLARS_WITH_CENTS"
        className={clsx(
          'shrink-0 text-body text-navy',
          semibold ? 'font-semibold' : 'font-regular',
        )}
      />
    </div>
  );
}
